import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { css } from '@aibi-ui/core';

export interface TypingEffectAttributes {
  getDisplayText: () => string;
}

export interface TypingEffectProps {
  content?: string;
  speed?: number;
  stopTyping?: boolean;
  isTyping?: boolean;
  onFinish?: () => void;
  onClick?: (
    el: HTMLElement | null,
    e: React.MouseEvent<HTMLSpanElement>,
  ) => void;
  className?: string;
}

const TypingEffect = forwardRef<TypingEffectAttributes, TypingEffectProps>(
  (
    {
      content = '',
      speed = 40,
      stopTyping = false,
      isTyping = true,
      onFinish,
      onClick,
      className,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLSpanElement>(null);

    /** 已提交到 DOM 的 HTML */
    const committedRef = useRef('');

    /** 等待打字的 HTML */
    const pendingRef = useRef('');

    /** 是否正在打字 */
    const typingRef = useRef(false);

    /** 字符定时器 */
    const timerRef = useRef<number | null>(null);

    useImperativeHandle(ref, () => ({
      getDisplayText: () => containerRef.current?.innerText || '',
    }));

    /* ========= 数据投喂（不碰 DOM） ========= */
    useEffect(() => {
      // 直接补全
      if (!isTyping || stopTyping) {
        clearTimer();
        typingRef.current = false;
        pendingRef.current = '';
        committedRef.current = content;

        if (containerRef.current) {
          containerRef.current.innerHTML = content;
        }

        onFinish?.();
        return;
      }

      const current = committedRef.current + pendingRef.current;

      const delta = content.slice(current.length);
      if (delta) {
        pendingRef.current += delta;
      }

      // 🔥 关键：只有这里尝试启动
      tryStartTyping();
    }, [content, isTyping, stopTyping]);

    const tryStartTyping = () => {
      if (typingRef.current) return;
      if (!pendingRef.current) return;
      startTypingBatch();
    };

    const clearTimer = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    /* ========= 单批次打字 ========= */
    const startTypingBatch = () => {
      if (!containerRef.current) return;
      if (!pendingRef.current) return;

      typingRef.current = true;

      const html = pendingRef.current;
      pendingRef.current = '';

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const nodes = Array.from(doc.body.childNodes);

      let nodeIndex = 0;

      const processNode = (
        node: Node,
        parent: HTMLElement,
        done: () => void,
      ) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || '';
          let i = 0;
          const textNode = document.createTextNode('');
          parent.appendChild(textNode);

          timerRef.current = window.setInterval(() => {
            textNode.textContent += text[i++];
            if (i >= text.length) {
              clearTimer();
              done();
            }
          }, speed);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node.cloneNode(false) as HTMLElement;
          parent.appendChild(el);

          const children = Array.from(node.childNodes);
          let idx = 0;

          const next = () => {
            if (idx < children.length) {
              processNode(children[idx++], el, next);
            } else {
              done();
            }
          };

          next();
        } else {
          done();
        }
      };

      const nextNode = () => {
        if (nodeIndex < nodes.length) {
          processNode(nodes[nodeIndex++], containerRef.current!, nextNode);
        } else {
          committedRef.current += html;
          typingRef.current = false;

          // 🔥 打完一批，看看还有没有
          if (pendingRef.current) {
            startTypingBatch();
          } else {
            onFinish?.();
          }
        }
      };

      nextNode();
    };

    const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
      const target = e.target as HTMLElement;
      const el = target.closest('[data-type]') as HTMLElement | null;
      onClick?.(el, e);
    };

    return (
      <span
        ref={containerRef}
        onClick={handleClick}
        className={className}
        css={theme => css`
          white-space: pre-wrap;

          [data-type] {
            cursor: pointer;
            color: ${theme.colors.primary.base};
            text-decoration: underline;
            text-underline-offset: 4px;
          }
        `}
      />
    );
  },
);

export default TypingEffect;
